import { useCallback, useEffect, useRef } from 'react';
import type { AlgorithmProblem, ClassDesignProblem, FullstackProblem } from '../data/problems';
import type { RunnableLanguage, TestResult } from '../utils/codeRunner';

// Drives jsRunner.worker.ts from the main thread. The headline feature is the
// timeout: if user code runs longer than DEADLINE_MS (infinite loop, runaway
// recursion), we terminate the worker and report a timeout instead of letting
// the tab freeze. A terminated worker is discarded; the next run lazily boots
// a fresh one.

const DEADLINE_MS = 5000;

export type SuiteOutcome =
  | { kind: 'results'; results: TestResult[] }
  | { kind: 'error'; error: string }
  | { kind: 'timeout' };

export type CustomOutcome =
  | { kind: 'custom'; output: string }
  | { kind: 'error'; error: string }
  | { kind: 'timeout' };

export interface JsWorkerController {
  runSuite: (
    problem: AlgorithmProblem | ClassDesignProblem,
    files: { name: string; code: string }[],
    language: RunnableLanguage,
  ) => Promise<SuiteOutcome>;
  runFullstack: (
    problem: FullstackProblem,
    files: { name: string; code: string }[],
  ) => Promise<SuiteOutcome>;
  runCustom: (
    code: string,
    inputJson: string,
    language: RunnableLanguage,
  ) => Promise<CustomOutcome>;
}

export function useJsWorker(): JsWorkerController {
  const workerRef = useRef<Worker | null>(null);
  const handlerRef = useRef<((data: Record<string, unknown>) => void) | null>(null);

  const ensureWorker = useCallback((): Worker => {
    if (!workerRef.current) {
      // webpack 5 (CRA 5) compiles this into a separate worker chunk.
      const worker = new Worker(new URL('./jsRunner.worker.ts', import.meta.url));
      worker.onmessage = ({ data }) => handlerRef.current?.(data);
      workerRef.current = worker;
    }
    return workerRef.current;
  }, []);

  // Kill the worker if it's still chewing on user code after DEADLINE_MS, then
  // null the ref so the next call spins up a fresh one.
  const killWorker = useCallback(() => {
    workerRef.current?.terminate();
    workerRef.current = null;
    handlerRef.current = null;
  }, []);

  useEffect(() => () => killWorker(), [killWorker]);

  const run = useCallback(<T,>(
    payload: Record<string, unknown>,
    onResult: (data: Record<string, unknown>) => T,
    timeoutValue: T,
  ): Promise<T> => {
    return new Promise<T>((resolve) => {
      const worker = ensureWorker();
      const id = crypto.randomUUID();
      const timer = window.setTimeout(() => {
        killWorker();
        resolve(timeoutValue);
      }, DEADLINE_MS);
      handlerRef.current = (data) => {
        if (data['id'] !== id) return;
        window.clearTimeout(timer);
        handlerRef.current = null;
        resolve(onResult(data));
      };
      worker.postMessage({ id, ...payload });
    });
  }, [ensureWorker, killWorker]);

  const runSuite = useCallback<JsWorkerController['runSuite']>(
    (problem, files, language) =>
      run<SuiteOutcome>(
        { kind: 'suite', problem, files, language },
        (data) =>
          data['error']
            ? { kind: 'error', error: data['error'] as string }
            : { kind: 'results', results: (data['results'] as TestResult[]) ?? [] },
        { kind: 'timeout' },
      ),
    [run],
  );

  const runFullstack = useCallback<JsWorkerController['runFullstack']>(
    (problem, files) =>
      run<SuiteOutcome>(
        { kind: 'fullstack', problem, files },
        (data) =>
          data['error']
            ? { kind: 'error', error: data['error'] as string }
            : { kind: 'results', results: (data['results'] as TestResult[]) ?? [] },
        { kind: 'timeout' },
      ),
    [run],
  );

  const runCustom = useCallback<JsWorkerController['runCustom']>(
    (code, inputJson, language) =>
      run<CustomOutcome>(
        { kind: 'custom', code, inputJson, language },
        (data) =>
          data['error']
            ? { kind: 'error', error: data['error'] as string }
            : { kind: 'custom', output: (data['custom'] as string) ?? '' },
        { kind: 'timeout' },
      ),
    [run],
  );

  return { runSuite, runFullstack, runCustom };
}
